import { useEffect } from 'react';

import { useContextMenu } from '../../hooks/useContextMenu';
import ajIcon from '/assets/aj-shouldersup.webp';

console.log('ContextMenuDemo component loaded');

const ContextMenuDemo = () => {
  const { 
    contextMenu, 
    hideContextMenu, 
    getMenuProps, 
    getMenuStyle 
  } = useContextMenu();

  // Log when the component mounts
  useEffect(() => {
    console.log('ContextMenuDemo mounted');
    return () => console.log('ContextMenuDemo unmounted');
  }, []);

  const menuItems = [
    { 
      label: 'Transcription', 
      icon: '✍️',
      action: () => window.open('https://talkudoc.com', '_blank')
    },
    { 
      label: 'Listen and Document', 
      icon: '🎙️',
      action: () => window.open('http://localhost:3001', '_blank')
    },
    { 
      label: 'Discuss', 
      icon: '💬',
      action: () => {
        // Toggle the AI chat window through the parent component
        const event = new CustomEvent('toggleAIChat');
        window.dispatchEvent(event);
      }
    }
  ];


  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="bg-blue-100 p-1 rounded-full border-2 border-blue-200">
            <img 
              src={ajIcon} 
              alt="AJ" 
              className="h-16 w-16 rounded-full"
            />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">AJ Assistant</h1>
            <p className="text-gray-600">Your Amazing Charts AI Assistant</p>
          </div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <p className="text-gray-700">
            Right-click in the area below to access AJ&lsquo;s features including transcription, documentation, and more.
          </p>
        </div>
        
        {/* Demo Area */}
        <div 
          className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center mb-8 bg-gray-50 hover:bg-blue-50 transition-colors"
          style={{ minHeight: '300px' }}
          {...getMenuProps()}
        >
          <div className="flex flex-col items-center justify-center h-full">
              <div className="relative mb-4">
                <img 
                  src={ajIcon} 
                  alt="AJ" 
                  className="h-16 w-16 rounded-full mx-auto mb-2 border-2 border-blue-200"
                />
                <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-700 mb-2">Right-Click for AJ Assistant</h2>
            <p className="text-gray-500">
              Try right-clicking anywhere in this area to see the context menu.
            </p>
          </div>
        </div>

        {/* Context Menu */}
        {contextMenu.visible && (
          <div 
            style={getMenuStyle()}
            className="bg-white shadow-xl rounded-md py-1 z-50 border border-gray-200 min-w-[200px]"
          >
            {menuItems.map((item, index) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  item.action();
                  hideContextMenu();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    item.action();
                    hideContextMenu();
                  }
                }}
                className="px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer flex items-center space-x-3"
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-100">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">How to Use</h3>
          <ol className="list-decimal pl-5 space-y-2 text-blue-700">
            <li>Right-click inside the dashed area above</li>
            <li>Select an action from the context menu</li>
            <li>Click outside or select an option to close the menu</li>
          </ol>
          
          <div className="mt-4 p-3 bg-white rounded border border-blue-100">
            <h4 className="font-medium text-blue-800 mb-2">Debug Info:</h4>
            <pre className="text-xs bg-gray-800 text-green-400 p-2 rounded overflow-auto">
              {JSON.stringify({
                menuVisible: contextMenu.visible,
                position: { x: contextMenu.x, y: contextMenu.y },
                viewport: {
                  width: window.innerWidth,
                  height: window.innerHeight
                }
              }, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextMenuDemo;
